'use client'
import { useTranslator } from '@/hooks'
import { mq, presets, rgbAlpha, vw } from '@/styles'
import { currencyFormatter, getDateFormatted, getDayOfWeekName, getDynamicText, IS_MMF_STORE, LANG_COUNTRY, LANG_REGION } from '@/utils'
import { FC } from 'react'
import styled from 'styled-components'
import { E_DELIVERY_TYPE, TypeCmsCart } from '@/schema'

type TypeCartSummary = {
  data: TypeCmsCart
  selectedDeliveryDate?: string | null
	noDeliveryDateOptions?: boolean
}

export type TypeValidDeliveryTypeMessages = 'project.cart.delivery_date_on_demand' | 'project.cart.delivery_date_frozen' | 'project.cart.delivery_date_stocked'

export const getDeliveryTypeFallback = (deliveryType: E_DELIVERY_TYPE) => {
  // Check if deliveryType is valid E_DELIVERY_TYPE enum value
  if (Object.values(E_DELIVERY_TYPE).includes(deliveryType)) return deliveryType.toLowerCase()
  // if not, returns .STOCKED as fallback
  return E_DELIVERY_TYPE.STOCKED.toLowerCase()
}

export const CartOverview: FC<TypeCartSummary> = ({ data, selectedDeliveryDate, noDeliveryDateOptions }) => {
  const {
    cartLines,
    subtotalExTax,
    totalExTax,
    deliveryDate,
    discountAmount,
    multipleDeliveryDates,
    deliverySchedules,
    shippingCostExTax,
  } = data
  const { t } = useTranslator()
  const schedule = Array.isArray(deliverySchedules)
    ? deliverySchedules[0]
    : undefined
  const agency = schedule?.agency
  const agencyText = schedule?.agencyText
  const selectedDate = selectedDeliveryDate ?? deliveryDate

  return (
    <Div className='small'>
      <h1>
        {t('project.auth.order.order_details.payment_summary')} (
        {cartLines.length})
      </h1>
      <table>
        <tbody>
          <tr>
            <td>{t('project.auth.order.order_details.subtotal_products')}</td>
            <td>
              {typeof subtotalExTax === 'number' &&
                currencyFormatter({
                  price: subtotalExTax,
                  langCountry: LANG_COUNTRY,
                  round: false,
                  trailingZeros: true,
                })}
              <span> +{t('project.auth.order.taxes')}</span>
            </td>
          </tr>
          {(typeof discountAmount === 'number' && discountAmount !== 0) && (
            <tr>
              <td>{t('project.auth.order.order_details.discount')}</td>
              <td>{currencyFormatter({
                price: discountAmount,
                langCountry: LANG_COUNTRY,
                round: false,
                trailingZeros: true,
              })}</td>
            </tr>
          )}
          <tr>
            <td>{t('project.auth.order.order_details.shipment')}</td>
            <td>
              {IS_MMF_STORE
                ? t('project.cart_summary_dropdown.free')
                : typeof shippingCostExTax === 'number'
                  ? currencyFormatter({
                      price: shippingCostExTax,
                      langCountry: LANG_COUNTRY,
                      round: false,
                      trailingZeros: true,
                    })
                  : t('project.cart_summary_dropdown.free')}
            </td>
          </tr>
          <tr>
            <td>{t('project.auth.order.order_details.total_products')}</td>
            <td>
              {typeof totalExTax === 'number' &&
                currencyFormatter({
                  price: totalExTax,
                  langCountry: LANG_COUNTRY,
                  round: false,
                  trailingZeros: true,
                })}
              <span> +{t('project.auth.order.taxes')}</span>
            </td>
          </tr>
        </tbody>
      </table>
      {deliveryDate && !agency && Array.isArray(cartLines) && cartLines.length > 0 && (
        <div className='delivery-date'>
          <SpanDeliveryDate>
						{noDeliveryDateOptions ? <></> :
							<>
								{getDynamicText(t('project.cart.delivery_date') as string, {
									deliveryDate: getDateFormatted(LANG_REGION, selectedDate),
								})}
							</>
						}
            {!multipleDeliveryDates ? (
              <SpanDeliveryDate className='opacity'>
                {getDynamicText(
                  t(`project.cart.delivery_date_${getDeliveryTypeFallback(cartLines[0]?.variant.deliveryType)}` as TypeValidDeliveryTypeMessages) as string,
                  {
                    deliveryDate: getDateFormatted(LANG_REGION, selectedDate),
                    onDemandDay: getDayOfWeekName(
                      LANG_REGION,
                      cartLines[0]?.variant.onDemandDay
                    ),
                    deliveryHour: cartLines[0]?.variant.deliveryType === E_DELIVERY_TYPE.ON_DEMAND ? '14h' : '21h',
                  }
                )}
              </SpanDeliveryDate>
            ) : (
              <SpanDeliveryDate className='opacity'>
                {getDynamicText(
                  t(`project.cart.delivery_date_${getDeliveryTypeFallback(cartLines[0]?.variant.deliveryType)}` as TypeValidDeliveryTypeMessages) as string,
                  {
                    deliveryDate: getDateFormatted(LANG_REGION, selectedDate),
                    onDemandDay: getDayOfWeekName(
                      LANG_REGION,
                      cartLines[0]?.variant.onDemandDay
                    ),
                    deliveryHour: cartLines[0]?.variant.deliveryType === E_DELIVERY_TYPE.ON_DEMAND ? '14h' : '21h',
                  }
                )}
              </SpanDeliveryDate>
            )}
          </SpanDeliveryDate>
        </div>
      )}
      {agency && Array.isArray(cartLines) && cartLines.length > 0 && (
        <div className='delivery-date'>
          <SpanDeliveryDate>{agencyText}</SpanDeliveryDate>
        </div>
      )}
    </Div>
  )
}

const Div = styled.div`
  background-color: var(--color-white);
  border-radius: ${vw(20, 'mobile')};
  margin-bottom: ${vw(10, 'mobile')};
  padding: ${vw(20, 'mobile')} 0;

  ${mq.greaterThan('nexus7')} {
    border-radius: ${vw(20, 'tablet')};
    margin-bottom: ${vw(10, 'tablet')};
    padding: ${vw(20, 'tablet')} 0;
  }

  ${mq.greaterThan('tablet')} {
    border-radius: ${vw(20, 'desktop')};
    margin-bottom: ${vw(30, 'desktop')};
    padding: ${vw(20, 'desktop')} 0;
  }

  ${mq.greaterThan('desktop')} {
    border-radius: 20px;
    margin-bottom: 30px;
    padding: 20px 0;
  }

  h1 {
    ${presets.font26_1}
    border-bottom: 1px solid ${rgbAlpha('--color-text--darker', 0.1)};
    text-align: left;
    padding: ${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(12, 'desktop')} ${vw(20, 'desktop')} ${vw(15, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 12px 20px 15px;
    }
  }

  h1,
  span,
  td {
    color: var(--color-text);
  }
  td {
    > span {
      opacity: 0.5;
    }
  }

  span,
  td {
    ${presets.font15_1B}
  }

  table {
    width: 100%;
    padding: ${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(12, 'desktop')} ${vw(20, 'desktop')} ${vw(15, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 12px 20px 15px;
    }

    tr {
      display: flex;
      justify-content: space-between;
      margin: 0;
      padding: ${vw(5, 'mobile')} 0;

      ${mq.greaterThan('nexus7')} {
        padding: ${vw(5, 'tablet')} 0;
      }

      ${mq.greaterThan('tablet')} {
        padding: ${vw(10, 'desktop')} 0;
      }

      ${mq.greaterThan('desktop')} {
        padding: 10px 0;
      }
    }
  }

  .delivery-date,
  .summary-total {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid ${rgbAlpha('--color-text--darker', 0.1)};
    padding: ${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(12, 'desktop')} ${vw(20, 'desktop')} ${vw(15, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 12px 20px 15px;
    }
  }

  .summary-total,
  .opacity {
    opacity: 0.4;
  }

  .opacity {
    ${presets.font10_1}
    color: var(--color-text--darker);
    margin-top: ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(10, 'tablet')};
    }
    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(10, 'desktop')};
    }
    ${mq.greaterThan('desktop')} {
      margin-top: 10px;
    }
  }
  .delivery-date {
    ${presets.font12_1}
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: space-between;

    > span {
      color: var(--color-text--darker);
    }
  }

  button {
    width: 100%;

    &[disabled],
    &.block {
      pointer-events: none;
      user-select: none;
      opacity: 0.5;
    }
  }
`

export const SpanDeliveryDate = styled.span`
  ${presets.font15}
  display: block;
`
